/**
 * Pure utility functions for permit preview panel
 *
 * These functions calculate what to display based on form data.
 * They are pure (no side effects) and easy to unit test.
 */
import type { PermitCreate } from '@/types/permits';

/**
 * Parse a date string (YYYY-MM-DD) as local time, not UTC.
 * Fixes timezone bug where "2025-12-31" becomes Dec 30 in local time.
 */
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export interface StatusInfo {
  label: string;
  explanation: string;
}

export interface RenewalInfo {
  text: string;
  isWarning: boolean;
}

/**
 * Determine permit status based on which date fields are filled.
 * Priority: closed > issued > submitted > not started
 */
export function getStatusInfo(formData: Partial<PermitCreate>): StatusInfo {
  if (formData.date_closed) {
    return {
      label: 'Closed',
      explanation: 'complete'
    };
  }

  if (formData.date_issued) {
    return {
      label: 'Issued',
      explanation: 'permit approved'
    };
  }

  if (formData.date_submitted) {
    return {
      label: 'Submitted',
      explanation: 'waiting on city'
    };
  }

  return {
    label: 'Not Started',
    explanation: 'no dates entered yet'
  };
}

/**
 * Determine renewal information based on recurring period and due date.
 */
export function getRenewalInfo(formData: Partial<PermitCreate>): RenewalInfo {
  const { recurring_period, due_date } = formData;

  // Check if due date is in the past
  if (due_date) {
    const dueDateTime = parseLocalDate(due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dueDateTime < today) {
      const formattedDate = dueDateTime.toLocaleDateString();
      return {
        text: `Expired on ${formattedDate}`,
        isWarning: true
      };
    }
  }

  // Recurring set but no due date - warning
  if (recurring_period && !due_date) {
    return {
      text: 'Set a due date to enable renewals',
      isWarning: true
    };
  }

  // Both set - show renewal info
  if (recurring_period && due_date) {
    const formattedDate = parseLocalDate(due_date).toLocaleDateString();
    return {
      text: `${recurring_period} — expires ${formattedDate}, renew button will appear`,
      isWarning: false
    };
  }

  // Due date only - one-time with expiration
  if (due_date && !recurring_period) {
    const formattedDate = parseLocalDate(due_date).toLocaleDateString();
    return {
      text: `One-time — expires ${formattedDate}, no renewal button`,
      isWarning: false
    };
  }

  // Neither set - one-time, no expiration
  return {
    text: 'One-time permit (no expiration tracked)',
    isWarning: false
  };
}

/**
 * Determine reminder information based on who is handling the permit.
 */
export function getReminderInfo(
  formData: Partial<PermitCreate>,
  applicantOrgName?: string
): string {
  const { applicant_organization_id, due_date } = formData;

  // External org is handling
  if (applicant_organization_id) {
    const orgName = applicantOrgName || 'External organization';
    return `${orgName} is handling — you won't get reminders`;
  }

  // Internal team is handling
  if (due_date) {
    return "You'll be notified at 60, 30, and 7 days before expiration";
  }

  return 'No expiration date — no reminders';
}
